import { Pressable, View } from "@-/com.npmjs.react-native";
import type { ChinaPlaceCategory } from "@-/dev.improve.haniverse.data.china-map";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import type { ReactNode } from "react";
import { ChinaMapPlaceGlyph } from "#china-map-place-glyph";
import type { ChinaMapPlaceView, ChinaMapProvinceView } from "#china-map-places";
import type { ChinaPlaceUnlockStatus } from "#china-place-unlock";

const CATEGORY_LABELS: Record<ChinaPlaceCategory, string> = {
  "autonomous-prefecture": "Autonomous prefecture",
  "autonomous-region": "Autonomous region",
  city: "City",
  county: "County",
  district: "District",
  "forestry-district": "Forestry district",
  league: "League",
  municipality: "Municipality",
  parish: "Parish",
  "planning-area": "Planning area",
  prefecture: "Prefecture",
  province: "Province",
  region: "Region",
  "special-administrative-region": "Special administrative region",
  town: "Urban township",
  township: "Township",
};

const STATUS_LABELS: Record<ChinaPlaceUnlockStatus, string> = {
  locked: "Locked",
  partial: "Partly learned",
  unlocked: "Unlocked",
};

function countUnlocked(views: readonly ChinaMapPlaceView[]): number {
  return views.filter((view) => view.unlock.status === "unlocked").length;
}

export function ChinaMapPlaceCard({
  actions,
  focusedProvince,
  onClose,
  onOpenCharacter,
  onSelectProvince,
  placeNounPlural,
  view,
}: {
  /** The travel row: start, travel, teleport, dragon, or why not yet. */
  actions: ReactNode;
  focusedProvince: ChinaMapProvinceView | null;
  onClose: () => void;
  onOpenCharacter: (character: string) => void;
  onSelectProvince: () => void;
  /** What the focused province's places are called, e.g. "cities". */
  placeNounPlural: string;
  view: ChinaMapPlaceView;
}) {
  const { levelIndex, status } = view.unlock;
  const isProvince = view.place === view.province;
  const studiedCount = view.unlock.characters.filter((character) => character.studied).length;
  return (
    <View className="gap-2 px-4 pt-1 pb-3" testID="china-map-place-card">
      <View className="flex-row items-start gap-3">
        <View className="flex-1 gap-0.5">
          <View className="flex-row flex-wrap items-baseline gap-2">
            <View className="flex-row" testID="china-map-place-name">
              {Array.from(view.displayName, (glyph, index) => {
                const character = view.unlock.characters.find((entry) => entry.character === glyph);
                const pinyin = view.place.pinyin[index] ?? "";
                return (
                  <Pressable
                    accessibilityLabel={`${glyph}, ${pinyin}, ${character?.studied ? "unlocked" : "locked"}`}
                    accessibilityRole="button"
                    hitSlop={4}
                    // eslint-disable-next-line react/no-array-index-key -- place-name glyphs retain reading order
                    key={index}
                    onPress={() => onOpenCharacter(glyph)}
                    testID={`china-map-character-${glyph}`}
                  >
                    <ChinaMapPlaceGlyph
                      className="text-3xl"
                      glyph={glyph}
                      pinyin={pinyin}
                      studied={character?.studied === true}
                    />
                  </Pressable>
                );
              })}
            </View>
            <Text className="shrink text-sm text-muted-foreground" numberOfLines={1}>
              {view.place.englishName === undefined
                ? view.place.name
                : `${view.place.name} · ${view.place.englishName}`}
            </Text>
          </View>
          <View className="flex-row flex-wrap items-center gap-x-1">
            <Text className="text-xs text-muted-foreground">
              {CATEGORY_LABELS[view.place.category]}
            </Text>
            {isProvince || focusedProvince === null ? null : (
              <Pressable
                accessibilityLabel={`Show ${focusedProvince.displayName}`}
                accessibilityRole="button"
                hitSlop={8}
                onPress={onSelectProvince}
              >
                <Text className="text-xs text-foreground underline">
                  in {focusedProvince.displayName}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
        <Pressable
          accessibilityLabel="Close"
          accessibilityRole="button"
          className="size-8 items-center justify-center rounded-full bg-muted active:bg-accent"
          hitSlop={8}
          onPress={onClose}
          testID="china-map-place-card-close"
        >
          <Icon className="text-foreground" name="MdClose" size={18} />
        </Pressable>
      </View>
      <View className="flex-row flex-wrap items-center gap-x-2">
        <Text className="text-base font-semibold text-foreground">
          {levelIndex === null ? "Not in the levels yet" : `Unlocks at Level ${levelIndex + 1}`}
        </Text>
        <Text
          className={cn(
            "text-sm",
            status === "unlocked" ? "text-activity-4" : "text-muted-foreground",
          )}
        >
          {status === "partial"
            ? `${studiedCount} of ${view.unlock.characters.length} characters learned`
            : STATUS_LABELS[status]}
        </Text>
      </View>
      {actions}
      {isProvince && focusedProvince !== null && focusedProvince.children.length > 0 ? (
        <Text className="text-xs text-muted-foreground">
          {countUnlocked(focusedProvince.children)} of {focusedProvince.children.length}{" "}
          {placeNounPlural} unlocked · Tap one to see its level
        </Text>
      ) : null}
    </View>
  );
}
